import { multiAudioOptions, secondsToString, STREAM_STATUS } from "constants/phase";
import { datafeedClipCuttingCategories, datafeedGraphicsProviders } from "constants/v2/datafeed";
import moment from "moment";
import { createSelector } from "reselect";
import { selectUserEntities } from "store/user/selectors";

import { selectClipDetailsById, selectClipStreamIdById } from "../clip/selectors";
import { streamEntityAdapter, streamSlice } from "./slice";

const selectStreamState = (state) => state[streamSlice.name];

export const selectStreamModals = createSelector(selectStreamState, (streamState) => streamState.modals);

export const selectStreamClipFilters = createSelector(selectStreamState, (streamState) => streamState.clipFilters);

export const selectChapterizeLanguages = createSelector(
  selectStreamState,
  (streamState) => streamState.chapterizeLanguages || [],
);

export const selectStreamPretemplates = createSelector(
  selectStreamState,
  (streamState) => streamState.preStreamTemplates || [],
);

export const selectValidPreStreamTemplates = createSelector(
  selectStreamPretemplates,
  (preStreamTemplates) => preStreamTemplates.filter((template) => template?.templateId) || [],
);

export const selectStreamPretemplatesCount = createSelector(
  selectStreamState,
  (streamState) => streamState.preStreamTemplatesCount || 0,
);

export const selectStreamIds = createSelector(selectStreamState, (streamState) =>
  streamEntityAdapter.getSelectors().selectIds(streamState),
);

export const selectCustomStreamIds = createSelector(selectStreamState, (streamState) => streamState.customIds || {});

export const selectLiveStreamIds = createSelector(selectCustomStreamIds, (customIds) => customIds.liveStreamIds || []);

export const selectRecentStreamIds = createSelector(
  selectCustomStreamIds,
  (customIds) => customIds.recentStreamIds || [],
);

export const selectStreamEntities = createSelector(selectStreamState, (streamState) =>
  streamEntityAdapter.getSelectors().selectEntities(streamState),
);

export const selectAllStreams = createSelector(selectStreamEntities, (streams) => Object.values(streams));

export const selectDropdownOptionsOfStream = createSelector(selectAllStreams, (allStreams) => {
  return allStreams.map((stream) => ({
    value: stream.streamId,
    label: `${stream.title} - ${stream.streamId}`,
  }));
});

export const selectStreamDetailsById = createSelector(
  selectStreamState,
  (state, props) => props.streamId,
  (streamState, streamId) => streamEntityAdapter.getSelectors().selectById(streamState, streamId) || null,
);

export const selectStreamCreatedAtById = createSelector(
  selectStreamDetailsById,
  (streamDetails) => streamDetails?.createdAt,
);

export const selectStreamDetailsByClipId = createSelector(
  selectStreamState,
  selectClipStreamIdById,
  (streamState, streamId) => streamEntityAdapter.getSelectors().selectById(streamState, streamId) || null,
);

export const selectClipActualDurationByStreamDetails = createSelector(
  selectStreamDetailsByClipId,
  selectClipDetailsById,
  (streamDetails, clipDetails) => {
    if (streamDetails?.streamStartTime && streamDetails?.fireOn && streamDetails?.isLive) {
      const fireOnTimestamp = streamDetails.initialMatchStartTimeOnServer;
      const matchStartTime = streamDetails.config.match_start_time;
      const resultInSeconds = matchStartTime - fireOnTimestamp;

      return secondsToString(clipDetails.initialStartTime - resultInSeconds);
    } else {
      return secondsToString(clipDetails?.initialStartTime);
    }
  },
);

export const selectTournamentIdByStreamDetails = createSelector(
  selectStreamDetailsByClipId,
  selectClipDetailsById,
  (streamDetails) => {
    return streamDetails?.fields?.tournamentId;
  },
);

const selectStreamDetailsByReferenceId = createSelector(
  selectStreamState,
  (state, props) => props.referenceStreamId || props.streamId,
  (streamState, referenceStreamId) =>
    streamEntityAdapter.getSelectors().selectById(streamState, referenceStreamId) || null,
);

export const selectStreamDataForTrimCut = createSelector(selectStreamDetailsByReferenceId, (streamDetails) => ({
  hlsS3URL: streamDetails?.hlsS3URL,
  s3RecordedUrl: streamDetails?.s3RecordedUrl,
  vod: streamDetails?.vod,
  previousRecordingURLs: streamDetails?.previousRecordingURLs,
  server_address: streamDetails?.server_address,
  seiEnable: streamDetails?.seiEnable,
  status: streamDetails?.status,
  glacierRecoveredStream: streamDetails?.glacierRecoveredStream,
  url: streamDetails?.url,
  id: streamDetails?._id,
  isMediaLive: streamDetails?.isMediaLive,
  streamId: streamDetails?.streamId,
  category: streamDetails?.category,
}));

export const selectStreamHlsS3URL = createSelector(
  selectStreamDetailsById,
  (streamDetails) =>
    streamDetails?.hlsS3URL ||
    `https://km-video-stream.s3.ap-south-1.amazonaws.com/vendor/stream/hls/${streamDetails?.streamId}/index.m3u8`,
);

export const selectLowestStreamHlsS3URL = createSelector(selectStreamDetailsById, (streamDetails) => {
  const liveVideoUrl =
    streamDetails?.hlsS3URL ||
    `https://km-video-stream.s3.ap-south-1.amazonaws.com/vendor/stream/hls/${streamDetails?.streamId}/index.m3u8`;

  return !streamDetails?.isMediaLive
    ? liveVideoUrl.replace("index.m3u8", "index_360p.m3u8")
    : liveVideoUrl.replace("master_highest.m3u8", "master_lowest.m3u8");
});

export const selectStreamLiveVideoUrl = createSelector(selectStreamDetailsById, (streamDetails) => {
  if (streamDetails?.vod) {
    return streamDetails?.url;
  } else if (streamDetails?.hlsS3URL) {
    return streamDetails.hlsS3URL;
  }

  return `https://km-video-stream.s3.ap-south-1.amazonaws.com/vendor/stream/hls/${streamDetails?.streamId}/index.m3u8`;
});

export const selectStreamPreviousRecordingURLs = createSelector(selectStreamDetailsById, (streamDetails) => {
  if (Array.isArray(streamDetails?.previousRecordingURLs) && streamDetails?.previousRecordingURLs.length > 0) {
    return streamDetails.previousRecordingURLs;
  } else {
    return null;
  }
});

export const selectStreamCategoryById = createSelector(
  selectStreamDetailsById,
  (streamDetails) => streamDetails?.category,
);

export const selectStreamEntityIdById = createSelector(
  selectStreamDetailsById,
  (streamDetails) => streamDetails?.entityId,
);

export const selectStreamAccessById = createSelector(
  selectStreamDetailsById,
  (streamDetails) => streamDetails?.streamAccess,
);

export const selectStreamClipCountById = createSelector(
  selectStreamDetailsById,
  (streamDetails) => streamDetails?.clipsCount || 0,
);

export const selectStreamTitleById = createSelector(selectStreamDetailsById, (streamDetails) => streamDetails?.title);

export const selectStreamFireOnById = createSelector(selectStreamDetailsById, (streamDetails) => streamDetails?.fireOn);

export const selectStreamEndDateTimeById = createSelector(
  selectStreamDetailsById,
  (streamDetails) => streamDetails?.endDateTime,
);

export const selectStreamStatusById = createSelector(selectStreamDetailsById, (streamDetails) => streamDetails?.status);

export const selectCalculatedStreamStatusById = createSelector(
  selectStreamStatusById,
  selectStreamFireOnById,
  (streamStatus, streamFireOn) => {
    if (streamFireOn !== "now" && moment(streamFireOn).isAfter() && streamStatus !== STREAM_STATUS.COMPLETED) {
      return streamStatus === STREAM_STATUS.PENDING ? STREAM_STATUS.SCHEDULED : streamStatus;
    }

    return streamStatus;
  },
);

export const selectStreamAllowAutoRating = createSelector(
  selectStreamDetailsById,
  (streamDetails) => streamDetails?.allowAutoRating || false,
);

export const selectStreamAllowedClipRating = createSelector(
  selectStreamDetailsById,
  (streamDetails) => streamDetails?.allowClipRating,
);

export const selectStreamClipRuleId = createSelector(
  selectStreamDetailsById,
  (streamDetails) => streamDetails?.clipRuleId,
);

export const selectStreamIsMedialive = createSelector(
  selectStreamDetailsById,
  (streamDetails) => streamDetails?.isMediaLive,
);

export const selectStreamIsBasketBallCategory = createSelector(
  selectStreamCategoryById,
  (streamCategory) => streamCategory === "basketball",
);

export const selectStreamIsCricketCategory = createSelector(
  selectStreamCategoryById,
  (streamCategory) => streamCategory === "cricket",
);

export const selectStreamReviewerUsers = createSelector(
  selectUserEntities,
  selectStreamDetailsById,
  (userEntities, streamDetails) => streamDetails?.reviewersId?.map((userId) => userEntities?.[userId]) || [],
);

export const selectStreamCount = createSelector(selectStreamState, (streamState) => streamState.count);

export const selectStreamAllFiltersModal = createSelector(selectStreamModals, (modals) => modals.streamAllFiltersModal);

export const selectStreamDeleteModal = createSelector(selectStreamModals, (modals) => modals.streamDeleteModal);

export const selectStreamReTriggerModal = createSelector(selectStreamModals, (modals) => modals.streamReTriggerModal);

export const selectStreamConvertVideoModal = createSelector(
  selectStreamModals,
  (modals) => modals.streamConvertVideoModal,
);

export const selectEndStreamModal = createSelector(selectStreamModals, (modals) => modals.endStreamModal);

export const selectDownloadMatchVideoModal = createSelector(
  selectStreamModals,
  (modals) => modals.downloadMatchVideoModal,
);

export const selectExportToCloudModal = createSelector(selectStreamModals, (modals) => modals.exportToCloudModal);

export const selectStreamScheduleLaterModal = createSelector(
  selectStreamModals,
  (modals) => modals.streamScheduleLaterDatePickerModal,
);

export const selectScheduleStreamCycleModal = createSelector(
  selectStreamModals,
  (modals) => modals.scheduleStreamCycleModal,
);

export const selectStreamClipFiltersById = createSelector(
  selectStreamClipFilters,
  (_state, props) => props.streamId,
  (streamClipFilters, streamId) => streamClipFilters?.[streamId],
);

export const selectStreamClipFilterRatings = createSelector(
  selectStreamClipFiltersById,
  (streamClipFilter) => streamClipFilter?.ratings,
);

export const selectStreamClipFilterEvents = createSelector(
  selectStreamClipFiltersById,
  (streamClipFilter) => streamClipFilter?.events,
);

export const selectStreamClipFilterGamePeriods = createSelector(
  selectStreamClipFiltersById,
  (streamClipFilter) => streamClipFilter?.gamePeriods,
);

export const selectStreamClipFilterPlayers = createSelector(
  selectStreamClipFiltersById,
  (streamClipFilter) => streamClipFilter?.players,
);

export const selectStreamClipFilterTeams = createSelector(
  selectStreamClipFiltersById,
  (streamClipFilter) => streamClipFilter?.teams,
);

export const selectStreamClipFilterIsBookmarked = createSelector(
  selectStreamClipFiltersById,
  (streamClipFilter) => streamClipFilter?.bookmarkedStats,
);
export const selectStreamClipFilterBookmarkedCount = createSelector(
  selectStreamClipFilterIsBookmarked,
  (streamClipFilterIsBookmarked) => streamClipFilterIsBookmarked?.find((item) => item.key === "true")?.count,
);
export const selectStreamClipFilterUnBookmarkedCount = createSelector(
  selectStreamClipFilterIsBookmarked,
  (streamClipFilterIsBookmarked) => streamClipFilterIsBookmarked?.find((item) => item.key === "false")?.count,
);
export const selectStreamClipFilterGameTimes = createSelector(
  selectStreamClipFiltersById,
  (streamClipFilter) => streamClipFilter?.gameTimes,
);

export const selectStreamClipFilterShotTypes = createSelector(
  selectStreamClipFiltersById,
  (streamClipFilter) => streamClipFilter?.shotTypes,
);

export const selectStreamClipFilterHeartbeats = createSelector(
  selectStreamClipFiltersById,
  (streamClipFilter) => streamClipFilter?.heartbeats,
);

export const selectStreamClipCustomFilterTags = createSelector(
  selectStreamClipFiltersById,
  (streamClipFilter) => streamClipFilter?.customTags,
);

export const selectStreamFieldsCategoryTypeById = createSelector(
  selectStreamDetailsById,
  (streamDetails) => streamDetails?.fields?.categoryType,
);
export const selectStreamFieldsTournamentById = createSelector(
  selectStreamDetailsById,
  (streamDetails) => streamDetails?.fields?.tournamentId,
);

export const selectStreamPromoCreationCountById = createSelector(
  selectStreamDetailsById,
  (streamDetails) => streamDetails?.promoCreationCount,
);

export const selectStreamMediaLiveConfigByStreamId = createSelector(
  selectStreamDetailsById,
  (streamDetails) => streamDetails?.mediaLiveConfig,
);

export const selectStreamselectedClipAudioByStreamId = createSelector(
  selectStreamMediaLiveConfigByStreamId,
  (mediaLiveConfig) => mediaLiveConfig?.selectedClipAudio,
);

export const selectStreamMediaLiveConfigInputTypeByStreamId = createSelector(
  selectStreamMediaLiveConfigByStreamId,
  (mediaLiveConfig) => mediaLiveConfig?.input_type,
);

export const selectStreamIsDatafeedPresent = createSelector(selectStreamDetailsById, (streamDetails) => {
  const isDatafeedPresent =
    streamDetails?.datafeed && streamDetails?.datafeed?.type && streamDetails?.datafeed?.matchId;

  return isDatafeedPresent;
});

export const selectStreamDatafeedMatchId = createSelector(selectStreamDetailsById, (streamDetails) => {
  return streamDetails?.datafeed?.matchId;
});

export const selectStreamInputurl = createSelector(selectStreamDetailsById, (streamDetails) => streamDetails?.url);
export const selectStreamMedialiveChannelId = createSelector(
  selectStreamDetailsById,
  (streamDetails) => streamDetails?.mediaLivechannelId,
);

export const selectStreamLanguage = createSelector(selectStreamDetailsById, (streamDetails) => {
  const language = multiAudioOptions.find((language) => language["alpha3-b"] === streamDetails?.language);

  return language?.alpha2 || "";
});

export const selectStreamDatafeedType = createSelector(selectStreamDetailsById, (streamDetails) => {
  return streamDetails?.datafeed?.type || "";
});

export const selectStreamIsTemplatesEnabled = createSelector(selectStreamDetailsById, (streamDetails) => {
  return Boolean(streamDetails?.datafeed && datafeedGraphicsProviders.includes(streamDetails?.datafeed?.type));
});

export const selectStreamIsClipCuttingEnabled = createSelector(selectStreamDetailsById, (streamDetails) => {
  const isDatafeedPresent = Boolean(
    streamDetails?.datafeed && streamDetails?.datafeed?.type && streamDetails?.datafeed?.matchId,
  );

  const isClipCuttingEnabled = Boolean(
    datafeedClipCuttingCategories?.[streamDetails?.datafeed?.type]?.includes(streamDetails?.category),
  );

  return isDatafeedPresent && isClipCuttingEnabled;
});

export const selectStreamOutputTemplateId = createSelector(selectStreamDetailsById, (streamDetails) => {
  return streamDetails?.outputTemplateId;
});

export const selectIsStreamCopied = createSelector(selectStreamDetailsByClipId, (streamDetails) => {
  return streamDetails?.isCopiedStream || false;
});

export const selectStreamFieldsById = createSelector(selectStreamDetailsById, (streamDetails) => streamDetails?.fields);

export const selectStreamCategories = createSelector(selectStreamEntities, (streams) => {
  return Array.from(new Set(Object.values(streams).map((stream) => stream.category)));
});
